// src/app/core/interceptor/auth.interceptor.ts

import { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '@services/auth.service';
import { throwError } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
	const authService = inject(AuthService);
	const { accessToken } = authService.getTokens();

	const excludedEndpoints = [
		'/auth/login',
		'/auth/logout',
		'/auth/verify-mfa',
		'/auth/forgot-password',
		'/auth/confirm-forgot-password',
		'/auth/change-password',
		'/en',
		'/es',
	];
	const isExcluded = excludedEndpoints.some((url) => req.url.includes(url));

	if (isExcluded) {
		return next(req);
	}

	if (req.url.includes('/refresh-token') || !accessToken) {
		return next(req);
	}

	if (!accessToken) {
		authService.removeTokens();
		return throwError(() => new Error('Failed to refresh token'));
	}

	if (authService.isTokenExpired()) {
		if (!authService.isRefreshing.value) {
			authService.isRefreshing.next(true);

			return authService.refreshToken().pipe(
				switchMap((tokenResponse: any) => {
					authService.isRefreshing.next(false);
					const newAccessToken = tokenResponse.data.accessToken;
					authService.refreshTokenSubject.next(newAccessToken);

					return next(addAuthHeader(req, newAccessToken));
				}),
				catchError((error) => {
					authService.isRefreshing.next(false);
					authService.removeTokens();
					return throwError(() => error);
				})
			);
		} else {
			return authService.refreshTokenSubject.pipe(
				filter((token) => token !== null),
				take(1),
				switchMap((newAccessToken) => {
					if (newAccessToken) {
						return next(addAuthHeader(req, newAccessToken));
					}
					return throwError(() => new Error('Failed to refresh token'));
				})
			);
		}
	}

	return next(addAuthHeader(req, accessToken));
};

function addAuthHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
	const currentOrg = 0;
	return request.clone({
		setHeaders: {
			Authorization: `Bearer ${token}`,
			...(currentOrg !== null && { 'CDT-ORG-ID': String(currentOrg) }),
		},
	});
}
